import React from 'react';
import { useQuery } from 'react-query';
import { history } from '../../../store';
import { fetchRecentSearches, fetchSearchedRecords } from './api';

export const useRecentSearches = () => {
  const { data: recentSearches = [] } = useQuery({
    queryKey: ['recentSearches'],
    queryFn: fetchRecentSearches,
    staleTime: 2 * 60 * 1000, // 2 minutes - good balance for UX
    cacheTime: 15 * 60 * 1000, // 15 minutes - longer cache to reduce API calls
    retry: 2,
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });

  return {
    recentSearches,
  };
};

export const useGlobalSearch = (organizationId, propertyId) => {
  const [searchResults, setSearchResults] = React.useState();
  const [loading, setLoading] = React.useState(false);
  const [currentPathname, setCurrentPathname] = React.useState(
    history.location.pathname,
  );

  const { recentSearches } = useRecentSearches();

  const searchQueries = React.useMemo(
    () => recentSearches?.map((search) => search.searchQuery) || [],
    [recentSearches],
  );

  const adjustSearchResults = (searchResults, showProperty) => {
    if (!searchResults?.people) return { people: [] };

    const getPersonLink = ({
      source,
      status,
      propertyId,
      id,
      applicationId,
      residentId,
    }) => {
      const linkMap = new Map([
        ['prospect', `/property/${propertyId}/prospect/${id}`],
        [
          'Applicant',
          applicationId
            ? `/property/${propertyId}/application/${applicationId}`
            : '',
        ],
        [
          'Current Resident',
          residentId ? `/property/${propertyId}/resident/${residentId}` : '',
        ],
        [
          'Prior Resident',
          residentId
            ? `/property/${propertyId}/prior-resident/${residentId}`
            : '',
        ],
      ]);
      return linkMap.get(source) || linkMap.get(status) || '';
    };

    const getUnitLink = ({ propertyId, unitId }) => {
      return propertyId && unitId
        ? `/property/${propertyId}/unit/${unitId}`
        : '';
    };

    return {
      ...searchResults,
      people: searchResults.people.map((person) => ({
        ...person,
        propertyName: showProperty ? person.propertyName : '',
        unitLink: getUnitLink(person),
        phone: person.phone,
        email: person.email || '---',
        personLink: getPersonLink(person),
      })),
    };
  };

  React.useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname !== currentPathname) {
        setCurrentPathname(location.pathname);
        setSearchResults(null);
      }
    });

    return unlisten;
  }, [currentPathname]);

  const handleSearch = async (searchTerm) => {
    setLoading(true);
    setSearchResults(null);

    try {
      const showProperty = propertyId ? false : true;
      const response = await fetchSearchedRecords(
        organizationId,
        propertyId,
        searchTerm,
      );
      setSearchResults(adjustSearchResults(response, showProperty));
    } catch (error) {
      console.error('Failed to fetch search results:', error);
      setSearchResults({ people: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (option) => {
    console.log('Selected option:', option);
  };

  return {
    searchResults,
    loading,
    currentPathname,
    handleSearch,
    handleSelect,
    recentSearches: searchQueries,
  };
};
